/* eslint-disable camelcase */
import React, { useState } from "react";
import PropTypes from "prop-types";
import PlaidLink from "react-plaid-link";
import { BANK_ACCOUNT_TYPES, APP_ENV, PLAID_KEY } from "@constants";
import { StripeService, PlaidService, PaymentService } from "@services";
import utils from "@utils";
import Loader from "../../Loader/Loader";

import "./BankAccountForm.scss";

function BankAccountForm({ id, onCancel, onSubmit, successCallback }) {
	const [form, setForm] = useState({
		account_holder_name: "",
		routing_number: "",
		account_number: "",
		account_number_confirm: "",
		account_holder_type: 1,
	});

	const [validations, setValidations] = useState({
		account_holder_name: false, // true = has error
		routing_number: false,
		account_number: false,
		account_number_confirm: false,
		account_holder_type: false,
	});

	const [manualForm, setManualForm] = useState(false);
	const [loading, setLoading] = useState(false);

	const bankAccountTypeOptions = Object.values(BANK_ACCOUNT_TYPES);
	const { lang } = global;

	/**
	 * Makes a request to create a new payment method in the RollKall database
	 * @param {String} TokenId A token representing the payment method
	 */
	const savePaymentMethod = TokenId => {
		PaymentService.addPaymentMethod({ BankAccount: { TokenId } })
			.then(() => {
				setLoading(false);
				utils.notify({
					type: "success",
					message: lang.t("Payment Method added successfully"),
				});

				if (successCallback) successCallback();
			})
			.catch(({ response }) => {
				setLoading(false);
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error adding Payment Method"),
				});
			});
	};

	/**
	 * @async
	 * Validates bank account details are present before submitting them to Stripe. This creates a payment method token when successful,
	 * which is saved to our database (see {@link savePaymentMethod}).
	 * @param {Event} event The HTML event handled by this method
	 */
	const onSubmitForm = async event => {
		if (event) event.preventDefault();

		// Validate form
		const newValidations = { ...validations };
		newValidations.account_holder_name = form.account_holder_name === "";
		newValidations.routing_number = form.routing_number === "";
		newValidations.account_number = form.account_number === "";
		newValidations.account_number_confirm =
			form.account_number_confirm === "" ||
			form.account_number !== form.account_number_confirm;
		newValidations.account_holder_type = form.account_holder_type === "";

		// If any validation property is true, the form is not valid
		const validationError = Object.values(newValidations).some(item => item);
		setValidations(newValidations);

		if (validationError) return;
		setLoading(true);

		const { error, token } = await StripeService.createBankAccountToken(form);

		if (error) {
			setLoading(false);
			utils.notify({
				type: "error",
				message: error.message ?? lang.t("Error getting token"),
			});
		} else if (onSubmit) {
			setLoading(false);
			onSubmit({ type: "bankAccount", token });
		} else {
			savePaymentMethod(token.id);
		}
	};

	/** Method that is called when a Plaid connection is successfully established. */
	const onPlaidSuccess = (token, metadata) => {
		setLoading(true);

		PlaidService.exchangeToken({ PublicToken: token, AccountId: metadata.account_id })
			.then(({ data }) => {
				if (onSubmit) {
					setLoading(false);
					onSubmit({ type: "bankAccount", token: { id: data.BankAccountToken } });
				} else {
					savePaymentMethod(data.BankAccountToken);
				}
			})
			.catch(() => {
				setLoading(false);

				utils.notify({
					type: "success",
					message: lang.t("Error getting token"),
				});
			});
	};

	const toggleBankForm = () => {
		setManualForm(state => !state.manualForm);
	};

	const onFormChange =
		(property, integer = false) =>
		event => {
			const value = event?.value ?? event?.target?.value ?? "";

			setForm(state => ({
				...state,
				[property]: integer ? value.replace(/\D/g, "") : value,
			}));
		};

	return (
		<div id={id}>
			<Choose>
				<When condition={manualForm}>
					<div
						className={`row form-group ${validations.account_holder_name ? "has-error" : ""}`}
					>
						<div className="col-xs-12">
							<label htmlFor="account_holder_name">{lang.t("Account Holder")}</label>
							<input
								type="text"
								id="account_holder_name"
								value={form.account_holder_name}
								onChange={onFormChange("account_holder_name")}
								className="form-control"
								placeholder="John Doe"
							/>
						</div>
					</div>

					<div
						className={`row form-group ${validations.routing_number ? "has-error" : ""}`}
					>
						<div className="col-xs-12">
							<label htmlFor="routing_number">{lang.t("Routing Number")}</label>
							<input
								type="text"
								id="routing_number"
								value={form.routing_number}
								onChange={onFormChange("routing_number", true)}
								className="form-control"
								maxLength={9}
								placeholder="110000000"
							/>
						</div>
					</div>

					<div className="row">
						<div
							className={`col-sm-6 col-xs-12 form-group ${validations.account_number ? "has-error" : ""}`}
						>
							<div>
								<label htmlFor="account_number">{lang.t("Account Number")}</label>
							</div>
							<input
								type="text"
								id="account_number"
								value={form.account_number}
								onChange={onFormChange("account_number", true)}
								className="form-control"
								placeholder="000123456789"
							/>
						</div>
						<div
							className={`col-sm-6 col-xs-12 form-group ${validations.account_number_confirm ? "has-error" : ""}`}
						>
							<div>
								<label htmlFor="account_number_confirm">
									{lang.t("Confirm Account Number")}
								</label>
							</div>
							<input
								type="text"
								id="account_number_confirm"
								value={form.account_number_confirm}
								onChange={onFormChange("account_number_confirm", true)}
								className="form-control"
								placeholder="000123456789"
							/>
						</div>
					</div>

					<div
						className={`row form-group ${validations.account_holder_type ? "has-error" : ""}`}
					>
						<div className="col-xs-12">
							<label htmlFor="account_holder_type">{lang.t("Account Type")}</label>
							<select
								type="text"
								id="account_holder_type"
								value={form.account_holder_type}
								onChange={onFormChange("account_holder_type")}
								className="form-control"
							>
								<For
									each="item"
									of={bankAccountTypeOptions}
								>
									<option
										key={`bank_account_type_${item.value}`}
										value={item.value}
									>
										{item.label}
									</option>
								</For>
							</select>
						</div>
					</div>
				</When>
				<Otherwise>
					<div
						id="plaid-container"
						className="media"
					>
						<PlaidLink
							clientName="RollKall"
							env={APP_ENV === "production" ? APP_ENV : "sandbox"}
							product={["auth"]}
							publicKey={PLAID_KEY}
							onExit={onCancel}
							onSuccess={onPlaidSuccess}
							language="en"
						>
							<div className="media-left media-middle">
								<div className="media-object">
									<i className="glyphicon glyphicon-link" />
								</div>
							</div>
							<div className="media-right media-body media-middle">
								<div className="h4 media-heading">
									<strong>{lang.t("Link Bank Account")}</strong>
								</div>
								<span className="text-muted">
									{lang.t("Sign in to your bank account using your credentials")}
								</span>
							</div>
						</PlaidLink>
						<br />
						<div
							className="media"
							id="manual-ach-link"
						>
							<button
								type="button"
								onClick={toggleBankForm}
							>
								<div className="media-left media-middle">
									<div className="media-object">
										<i className="glyphicon glyphicon-edit" />
									</div>
								</div>
								<div className="media-right media-body media-middle">
									<div className="h4 media-heading">
										<strong>{lang.t("Manually Add Account")}</strong>
									</div>
									<span className="text-muted">
										{lang.t("Add account using a routing and account number")}
									</span>
								</div>
							</button>
						</div>
						<Loader active={loading} />
					</div>
				</Otherwise>
			</Choose>

			<div className="row footer-row">
				<div className="col-xs-12 text-right">
					<button
						type="button"
						className="btn btn-link"
						onClick={onCancel}
					>
						{lang.t("Cancel")}
					</button>
					<If condition={manualForm}>
						<button
							type="button"
							disabled={loading}
							onClick={onSubmitForm}
							className="btn btn-primary"
						>
							{loading ? lang.t("Loading") : lang.t("Save")}
						</button>
					</If>
				</div>
			</div>
		</div>
	);
}

BankAccountForm.propTypes = {
	id: PropTypes.string,
	onCancel: PropTypes.func,
	onSubmit: PropTypes.func,
	successCallback: PropTypes.func,
};

BankAccountForm.defaultProps = {
	id: "bank-account-form",
	onCancel: undefined,
	onSubmit: undefined,
	successCallback: undefined,
};

export default BankAccountForm;
